import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import InputText from "../../../components/Input/InputText";
import ErrorText from "../../../components/Typography/ErrorText";
import { showNotification } from "../../common/headerSlice";
import { addNewBulletin } from "../bulletinSlice";
import api from "../../../utils/axios";
import { refreshBulletins } from "../BulletinsContext";

const INITIAL_MESSAGE_OBJ = {
  date: "",
  pdfUrl: "",
};

async function addBulletin(e) {
  try {
    const newBulletin = await api.post("api/weeklyBulletins", e);
    return newBulletin;
  } catch (error) {
    console.error("Error adding bulletin:", error);
    throw error;
  }
}

function AddBulletinModalBody({ closeModal }) {
  const dispatch = useDispatch();
  const [errorBulletin, setErrorBulletin] = useState("");
  const [bulletinObj, setBulletinObj] = useState(INITIAL_MESSAGE_OBJ);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pdfUrlKey, setPdfUrlKey] = useState(0); // Key to force PDF InputText re-render
  const fileInputRef = useRef(null);

  const saveNewBulletin = async () => {
    //to do: make date a date field...
    const objDate = new Date(bulletinObj.date.trim());
    if (bulletinObj.date.trim() === "")
      return setErrorBulletin("Date is required!");
    else if (objDate.toString() === "Invalid Date")
      return setErrorBulletin("Date is invalid!");

    // If file is selected but no URL, we need to upload it first
    // If URL is provided, we can proceed directly
    let finalPdfUrl = bulletinObj.pdfUrl;
    setSaving(true);
    setErrorBulletin("");

    try {
      // Upload file first if one is selected and no URL provided
      // If URL is provided, it takes precedence over the selected file
      if (selectedFile && !bulletinObj.pdfUrl.trim()) {
        if (!bulletinObj.date.trim()) {
          setSaving(false);
          return setErrorBulletin("Date is required for file upload!");
        }

        const formData = new FormData();
        formData.append("pdfFile", selectedFile);
        formData.append("date", bulletinObj.date);

        const uploadResponse = await api.post(
          "/api/weeklyBulletins/upload-pdf",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 300000, // 5 minutes timeout for large file uploads
            onUploadProgress: (progressEvent) => {
              // Optional: Could show upload progress here
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              console.log(`Upload progress: ${percentCompleted}%`);
            },
          },
        );

        finalPdfUrl = uploadResponse.data.url;
        // Update the URL field to show the uploaded file's public URL
        setBulletinObj({ ...bulletinObj, pdfUrl: finalPdfUrl });
        // Clear selected file so URL field becomes visible
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        // Force InputText to re-render with new URL value
        setPdfUrlKey((prev) => prev + 1);
      } else if (bulletinObj.pdfUrl.trim()) {
        // Use provided URL (file will be ignored if one was selected)
        finalPdfUrl = bulletinObj.pdfUrl;
      } else {
        setSaving(false);
        return setErrorBulletin(
          "Either a PDF or a PDF URL is required!",
        );
      }

      // Create the bulletin
      let newBulletinObj = {
        date: bulletinObj.date,
        pdfUrl: finalPdfUrl,
      };

      await addBulletin(newBulletinObj);
      dispatch(addNewBulletin({ newBulletinObj }));
      dispatch(showNotification({ bulletin: "New Bulletin Added!", status: 1 }));
      closeModal();
      refreshBulletins();
    } catch (error) {
      console.error("Error saving bulletin:", error);
      const errorMsg =
        error.response?.data?.bulletin ||
        "Failed to save bulletin. Please try again.";
      setErrorBulletin(errorMsg);
      dispatch(
        showNotification({
          bulletin: errorMsg,
          status: 0,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const updateFormValue = ({ updateType, value }) => {
    setErrorBulletin("");
    // If URL is being updated and it has a value, clear the selected file
    if (updateType === "pdfUrl" && value.trim()) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    setBulletinObj({ ...bulletinObj, [updateType]: value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const allowedTypes = [
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      console.log(file.type)
      setErrorBulletin("Invalid file type. Only pdf files are allowed.");
      e.target.value = ""; // Reset file input
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setErrorBulletin("");
    // Clear URL when file is selected
    setBulletinObj({ ...bulletinObj, pdfUrl: "" });
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Clear URL field when file is removed
    setBulletinObj({ ...bulletinObj, pdfUrl: "" });
  };

  return (
    <>
      <InputText
        type="date"
        defaultValue={bulletinObj.date ? bulletinObj.date.slice(0, 10) : ""}
        updateType="date"
        containerStyle="mt-4"
        labelTitle="Date"
        updateFormValue={updateFormValue}
      />

      <div className={`form-control w-full mt-4`}>
        <label className="label">
          <span className="label-text text-base-content">
            Upload PDF{" "}
            {bulletinObj.pdfUrl.trim() && "(disabled - URL entered)"}
          </span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept="pdf/*"
            onChange={handleFileSelect}
            disabled={saving || !!bulletinObj.pdfUrl.trim()}
            className="file-input file-input-bordered w-full max-w-xs"
          />
        </div>
        {selectedFile && (
          <label className="label">
            <span className="label-text-alt text-info">
              Selected: {selectedFile.name} (will upload on save)
            </span>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={handleRemoveFile}
              disabled={saving}
            >
              Remove
            </button>
          </label>
        )}
      </div>

      <InputText
        key={pdfUrlKey}
        type="url"
        defaultValue={bulletinObj.pdfUrl}
        updateType="pdfUrl"
        containerStyle="mt-4"
        labelTitle="PDF URL (or upload file above)"
        placeholder={
          selectedFile
            ? "File selected - URL disabled"
            : "Enter PDF URL or upload file"
        }
        updateFormValue={updateFormValue}
        disabled={!!selectedFile || saving}
      />
      
      <ErrorText styleClass="mt-16">{errorBulletin}</ErrorText>
      <div className="modal-action">
        <button className="btn btn-ghost" onClick={() => closeModal()}>
          Cancel
        </button>
        <button
          className="btn btn-primary px-6"
          onClick={() => saveNewBulletin()}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </>
  );
}

export default AddBulletinModalBody;
